import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Router, { useRouter } from 'next/router';
import { useTranslations, useLocale } from 'next-intl';

import StyledLoginMask from './loginMask.style';

import Button from '../../atoms/button/button';
import Copy from '../../atoms/copy/copy';
import Headline from '../../atoms/headline/headline';
import MixedCopy from '../../molecules/mixed-copy/mixedCopy';
import TokenInput from '../../atoms/token-input/tokenInput';
import LoginIcon from '../../icons/login.svg';
import { fetchCsrfToken } from '../../../helper/api';

const LoginMask = ({
  action,
  headline,
  logoutHeadline,
  subline,
  copy,
  hintText,
  input,
  currentTokenId,
  isLoggedIn = false,
  updateLogin = () => {},
  insideNavigation = false,
  isTeaserCard = false,
  ...props
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const [tokenId, setTokenId] = useState('');
  const [error, setError] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');

  const router = useRouter();
  const { pathname } = router || {};

  const handleRouteChange = id => {
    updateLogin(true, id);

    const languageSpecific = locale === 'de' ? '/de/meine-ausstellung/' : '/en/my-exhibition/';

    Router.push(
      { pathname: '/token', query: { lang: locale, tokenId: id.toLowerCase(), page: 1 } },
      `${languageSpecific}${id.toLowerCase()}`
    ).then(() => window.scrollTo(0, 0));
  };

  const handleLogout = () => {
    updateLogin(false, '');

    if (pathname === '/token') {
      Router.push(
        { pathname: '/time', query: { lang: locale, slug: 'index', filters: null, page: 1 } },
        `/${locale}/`
      ).then(() => window.scrollTo(0, 0));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    // Check if tokenID is empty
    if (tokenId === '') {
      setError(true);
      return;
    }

    // Check if is development
    if (process.env.NODE_ENV === 'development') {
      handleRouteChange(tokenId);
      return;
    }

    // Check if token exists, then login, else show error
    fetch('/de/api/token/login', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token: tokenId
      })
    })
      .then(res => res.json())
      .then(({ message, token }) => {
        if (message === 'Success!') {
          handleRouteChange(tokenId);
        } else {
          setError(true);
          setCsrfToken(token);
        }
      })
      .catch(() => setError(true));
  };

  // Fetch CSRF Token for token id check
  useEffect(
    () =>
      fetchCsrfToken(
        token => setCsrfToken(token),
        () => setError(true)
      ),
    []
  );

  return (
    <StyledLoginMask
      {...props}
      insideNavigation={insideNavigation}
      isTeaserCard={isTeaserCard}
      isDarkTheme={pathname === '/exhibition' || pathname === '/ausstellung'}
    >
      {isTeaserCard && <LoginIcon className="loginMask__icon" />}
      {pathname !== '/token' ? (
        <div className="loginMask__content">
          {((!isTeaserCard && subline) || (isTeaserCard && !isLoggedIn && subline)) && (
            <Headline tag="h3" subline>
              {subline}
            </Headline>
          )}
          {(!isTeaserCard || (isTeaserCard && !isLoggedIn)) && (
            <Headline className="loginMask__headline" secondary>
              {!isLoggedIn ? headline : logoutHeadline}
            </Headline>
          )}

          {!isLoggedIn && copy && <MixedCopy fullWidth html={copy} />}
        </div>
      ) : null}

      <div className="loginMask__form-wrapper">
        {isLoggedIn ? (
          <>
            <div className="loginMask__current-token">
              <Copy small>
                <span className="loginMask__token-label">
                  {t('loginMask.myExhibitionCode')}
                  {': '}
                </span>
                <span className="loginMask__token">{currentTokenId}</span>
              </Copy>
            </div>
            <div className="loginMask__buttons">
              {pathname !== '/token' && (
                <Button onClick={() => handleRouteChange(currentTokenId)} noBorder arrowLeft>
                  {t('loginMask.myExhibition')}
                </Button>
              )}
              <Button onClick={() => handleLogout()} noBorder arrowLeft>
                Logout
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={e => handleSubmit(e)} className="loginMask__form" noValidate>
            <TokenInput
              className="loginMask__input"
              onFocus={() => setError(false)}
              onChange={e => setTokenId(e.target.value)}
              name={input.name}
              label={t('loginMask.myExhibitionCode')}
              value={tokenId}
              error={error}
              placeholder={input.placeholder}
              required={input.required}
              insideNavigation={insideNavigation}
            />
            <div className="loginMask__buttons">
              <Button type="submit" noBorder arrowLeft>
                Login
              </Button>
            </div>
            {!isLoggedIn && (
              <Copy className="loginMask__hint" small>
                {hintText}
              </Copy>
            )}
          </form>
        )}
        {!isTeaserCard && <LoginIcon className="loginMask__icon" />}
      </div>
    </StyledLoginMask>
  );
};

LoginMask.propTypes = {
  action: PropTypes.string,
  headline: PropTypes.string,
  logoutHeadline: PropTypes.string,
  subline: PropTypes.string,
  copy: PropTypes.string,
  hintText: PropTypes.string,
  input: PropTypes.shape(),
  isLoggedIn: PropTypes.bool,
  updateLogin: PropTypes.func,
  currentTokenId: PropTypes.string,
  insideNavigation: PropTypes.bool,
  isTeaserCard: PropTypes.bool
};

export default LoginMask;
